import AsyncStorage from '@react-native-async-storage/async-storage'
import jwtDecode from 'jwt-decode'

import { CompleteUser, Profile, StrapiUser } from '../../@types'
import { CLIENT_ID, JWT_REFRESH_BEFORE_EXPIRATION_SECONDS } from '../../constants/config'
import { AccountType } from '../../hooks/use-create-standard-profile'
import { restartApp } from '../../util/restart'
import { endSession } from '../../util/sentry'
import { removeWhiteSpaces } from '../../util/string'
import { getReferralSlug } from '../referral'
import { isMountedRef, navigationRef } from '../refs'
import { apiRequest } from '../request'

export type SocialLoginAuthRequestPayload = {
  isNewUser: boolean
  accessToken: string
  refreshToken?: string
  socialAccountData?: StrapiUser
  maskedPhone?: string
  sessionId: string
}

type TokenPayload = {
  id: number
  exp: number
}

export async function getSessionId() {
  return AsyncStorage.getItem('sessionId')
}

export async function getToken() {
  const token = await AsyncStorage.getItem('token')

  if (shouldRefresh(token)) {
    const tokens = await refreshTokens()
    return tokens?.accessToken ?? null
  }

  return token
}

export async function saveToken(key: string, token: string) {
  await AsyncStorage.setItem(key, token)
}

export async function removeToken() {
  await AsyncStorage.multiRemove(['token', 'refreshToken', 'sessionId'])
}

export const register = {
  phoneExists(phone: string) {
    return apiRequest<{
      phoneExists: boolean
    }>('/auth/register/phone-exists', {
      body: { phone: removeWhiteSpaces(phone) },
    })
  },

  phone(phone: string) {
    return apiRequest<{
      accessToken: string
    }>('/auth/register/phone', {
      body: { phone: removeWhiteSpaces(phone) },
    })
  },

  confirmation(code: string, token: string) {
    return apiRequest<{ accessToken: string }>('/auth/register/confirmation', {
      body: { code },
      headers: { Authorization: `Bearer ${token}` },
    })
  },

  async patientValidate(profile: Profile, token: string, accountType: AccountType) {
    const referralSlug = await getReferralSlug()
    return apiRequest<Tokens>('/auth/register/patient/validate', {
      body: { accountType, profile, referralSlug },
      headers: { Authorization: `Bearer ${token}` },
    })
  },

  async patient(profile: Profile, token: string, accountType: AccountType) {
    const referralSlug = await getReferralSlug()
    return apiRequest<Tokens>('/auth/register/patient', {
      body: { accountType, profile, referralSlug },
      headers: { Authorization: `Bearer ${token}` },
    })
  },

  async dependant(profile: Profile) {
    return apiRequest<StrapiUser>('/auth/register/dependant', {
      body: { profile },
      getToken,
    })
  },
}

type LoginSuccessResponse = Tokens & { maskedPhone: string }
type MaxSessionsReachedResponse = { maxSessionsToken?: string }

export const socialLogin = {
  auth(provider: string, accessToken: string) {
    return apiRequest<SocialLoginAuthRequestPayload>(`/auth/social-login?clientId=${CLIENT_ID}`, {
      body: { provider, accessToken },
      method: 'POST',
    })
  },
}

export const login = {
  callback(phone: string, password: string) {
    return apiRequest<LoginSuccessResponse | MaxSessionsReachedResponse>(`/auth/login?clientId=${CLIENT_ID}`, {
      body: { identifier: removeWhiteSpaces(phone), password },
    })
  },

  mfa(code: string, token: string) {
    return apiRequest<Tokens | MaxSessionsReachedResponse>('/auth/login/mfa', {
      body: { code },
      headers: { Authorization: `Bearer ${token}` },
    })
  },

  forceLogin(maxSessionsToken: string) {
    return apiRequest<Tokens>('/auth/login/force', {
      method: 'post',
      body: {},
      headers: { Authorization: `Bearer ${maxSessionsToken}` },
    })
  },
}

export const recover = {
  phone(phone: string) {
    return apiRequest<{ accessToken: string }>('/auth/recover', {
      body: { phone: removeWhiteSpaces(phone) },
    })
  },

  confirmation(code: string, token: string) {
    return apiRequest<{ accessToken: string }>('/auth/recover/confirmation', {
      body: { code },
      headers: { Authorization: `Bearer ${token}` },
    })
  },

  password(password: string, token: string) {
    return apiRequest<{ ok: true }>('/auth/recover/password', {
      body: { password },
      headers: { Authorization: `Bearer ${token}` },
    })
  },
}

type ResendMfaCodeResponse = {
  accessToken: string
  maskedPhone: string
}

export const mfa = {
  resendViaSMS(token: string) {
    return apiRequest<ResendMfaCodeResponse>('/auth/mfa/resend-via-sms', {
      body: null,
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
  },

  resendViaPhoneCall(token: string) {
    return apiRequest<ResendMfaCodeResponse>('/auth/mfa/resend-via-phone-call', {
      body: null,
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
  },
}

export async function logout() {
  // Don't wait for the logout call to complete because we can proceed with the
  // logout even if it fails.
  apiRequest(`/auth/logout`, { body: {}, getToken }).catch(error => {
    console.error('🔴 Logout call error.', error)
    // This error can be ignored because for a patient the only consequence of
    // failing this request is that the user will see the force login dialog if it
    // tries to log in *before the session expires*.
  })
  await removeToken()
  await endSession()
  if (isMountedRef.current) {
    navigationRef?.resetRoot({
      index: 0,
      routes: [{ name: 'Welcome' }],
    })
  }
}

export async function createUserProfile(profile: Profile, accountType: AccountType) {
  return apiRequest<Profile>('/patients', {
    body: { accountType, profile },
    getToken,
  })
}

export async function deleteUserProfile(patientId: number) {
  return apiRequest<Profile>(`/patients/${patientId}`, {
    method: 'DELETE',
    getToken,
  })
}

export async function getUser() {
  return apiRequest<CompleteUser>('/user/me', {
    getToken,
  })
}

function shouldRefresh(token: string | null) {
  if (!token) return false

  const decodedJwt = jwtDecode<TokenPayload>(token)

  return decodedJwt.exp - Date.now() / 1000 < JWT_REFRESH_BEFORE_EXPIRATION_SECONDS
}

export const isMfaPending = (token: string): boolean => {
  const decodedJwt: Record<string, string> | undefined = jwtDecode(token)
  return decodedJwt?.mfa === 'PENDING'
}

async function renew() {
  const token = await AsyncStorage.getItem('refreshToken')

  if (!token) return null

  const { data, error } = await apiRequest<Tokens>(`/auth/refresh`, {
    getToken: async () => token,
  })

  if (error || !data) return null

  saveToken('token', data.accessToken)
  saveToken('refreshToken', data.refreshToken)
  saveToken('sessionId', data.sessionId)

  return data
}

export type Tokens = { accessToken: string; refreshToken: string; sessionId: string }

let refreshPromise: Promise<Tokens | null> | null

export async function refreshTokens() {
  if (!refreshPromise) refreshPromise = renew()

  return refreshPromise.then(d => {
    refreshPromise = null
    if (!d) {
      logout().finally(restartApp)
    }
    return d
  })
}
